import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  useTheme,
  Badge,
} from "@mui/material";
import {
  Bed,
  LocalHospital,
  MeetingRoom,
  TrendingUp,
  Warning,
  CheckCircle,
  History,
  ArrowForward,
  Home,
  Hotel,
} from "@mui/icons-material";
import { useAuth } from "../../../context";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { listWards, listRooms, listBeds } from "../../../api/bedManagement";

// Widget component matching reception style
const BedWidget = ({ 
  icon, 
  title, 
  value, 
  amount, 
  history = [], 
  onClick,
  cardSx = {},
  isSummaryCard = false 
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const capitalizeTitle = (str) => {
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  return (
    <Card
      onClick={onClick}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        cursor: history?.length > 0 ? "pointer" : "default",
        transition: "all 0.3s ease",
        border: 0,
        borderRadius: 4,
        boxShadow: isSummaryCard ? "0 8px 32px 0 rgba(31, 38, 135, 0.15)" : 2,
        p: isSummaryCard ? 4 : 2,
        background: isSummaryCard
          ? `linear-gradient(135deg, ${cardSx?.bgcolor} 60%, ${theme.palette.background.paper} 100%)`
          : cardSx?.bgcolor || theme.palette.primary.main,
        color: cardSx?.color || theme.palette.primary.contrastText,
        position: "relative",
        borderTop: isSummaryCard
          ? `3px solid ${cardSx?.borderLeft || cardSx?.bgcolor}`
          : undefined,
        borderBottom: isSummaryCard
          ? `3px solid ${cardSx?.borderLeft || cardSx?.bgcolor}`
          : undefined,
        ...cardSx,
        "&:hover": {
          boxShadow: history?.length > 0 ? theme.shadows[8] : undefined,
          transform:
            history?.length > 0 ? "translateY(-4px) scale(1.03)" : undefined,
        },
      }}
      aria-label={`${title} card`}
      role={history?.length > 0 ? "button" : undefined}
      tabIndex={history?.length > 0 ? 0 : -1}
      onKeyDown={(e) => {
        if (history?.length > 0 && (e.key === "Enter" || e.key === " ")) {
          onClick();
        }
      }}
    >
      {/* Icon in a circle */}
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: isSummaryCard
            ? cardSx?.borderLeft || cardSx?.bgcolor
            : theme.palette.primary.contrastText,
          color: isSummaryCard
            ? cardSx?.color || "#fff"
            : theme.palette.primary.main,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 36,
          boxShadow: isSummaryCard ? 3 : 1,
          mb: 2,
        }}
      >
        {icon}
      </Box>
      {/* Title */}
      <Typography
        variant="h6"
        fontWeight={700}
        sx={{
          color: cardSx?.color || theme.palette.primary.contrastText,
          textAlign: "center",
          mb: 1,
          letterSpacing: 1,
          fontSize: 22,
          textShadow: isSummaryCard ? "0 2px 8px rgba(0,0,0,0.10)" : undefined,
        }}
      >
        {capitalizeTitle(title)}
      </Typography>
      {/* Value/Amount */}
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{
            color: cardSx?.color || theme.palette.primary.contrastText,
            fontSize: 32,
            textShadow: isSummaryCard ? "0 2px 8px rgba(0,0,0,0.10)" : undefined,
          }}
        >
          {value}
        </Typography>
        {amount && (
          <Typography
            variant="body1"
            sx={{
              color: cardSx?.color || theme.palette.primary.contrastText,
              opacity: 0.9,
              fontSize: 16,
              mt: 1,
            }}
          >
            {amount}
          </Typography>
        )}
      </Box>
      {/* History badge: only for non-summary cards */}
      {!isSummaryCard && (
        <Box sx={{ position: "absolute", top: 16, right: 16 }}>
          <Badge
            badgeContent={history?.length || 0}
            color="warning"
            sx={{ mr: 1 }}
          >
            <History
              sx={{
                color: cardSx?.color || theme.palette.primary.contrastText,
                mr: 1,
                fontSize: 22,
              }}
            />
          </Badge>
        </Box>
      )}
      {/* View Details button at the bottom of the card */}
      {!isSummaryCard && history?.length > 0 && (
        <Box
          sx={{
            mt: "auto",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            mb: 1,
          }}
        >
          <Button
            variant="contained"
            size="small"
            endIcon={<ArrowForward />}
            sx={{
              borderRadius: 999,
              px: 3,
              fontWeight: 700,
              background:
                theme.palette.mode === "light"
                  ? theme.palette.grey[200]
                  : theme.palette.grey[800],
              color: theme.palette.text.primary,
              boxShadow: 1,
              textTransform: "none",
              "&:hover": {
                background: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
              },
            }}
            onClick={onClick}
          >
            {t("bedManagement.viewDetails", "View Details")}
          </Button>
        </Box>
      )}
    </Card>
  );
};

const BedDashboard = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  // Fetch all data - Optimized: Dashboard can use larger page size but still paginated
  const { data: wardsData, isLoading: wardsLoading } = useQuery({
    queryKey: ["wards-dashboard"],
    queryFn: () => listWards({ page_size: 200 }, token),
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
  });

  const { data: roomsData, isLoading: roomsLoading } = useQuery({
    queryKey: ["rooms-dashboard"],
    queryFn: () => listRooms({ page_size: 200 }, token),
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
  });

  const { data: bedsData, isLoading: bedsLoading } = useQuery({
    queryKey: ["beds-dashboard"],
    queryFn: () => listBeds({ page_size: 200 }, token),
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
  });

  const isLoading = wardsLoading || roomsLoading || bedsLoading;

  // Calculate statistics
  const getStats = () => {
    if (!bedsData || !roomsData || !wardsData) return null;

    // Handle paginated responses
    const beds = Array.isArray(bedsData) ? bedsData : bedsData?.results || [];
    const rooms = Array.isArray(roomsData) ? roomsData : roomsData?.results || [];
    const wards = Array.isArray(wardsData) ? wardsData : wardsData?.results || [];

    const totalBeds = beds.length;
    const availableBeds = beds.filter((bed) => bed.status === 1).length;
    const occupiedBeds = beds.filter((bed) => bed.status === 2).length;
    const cleaningBeds = beds.filter((bed) => bed.status === 3).length;
    const maintenanceBeds = beds.filter((bed) => bed.status === 4).length;
    const totalRooms = rooms.length;
    const totalWards = wards.length;
    const occupancyRate =
      totalBeds > 0 ? Math.round((occupiedBeds / totalBeds) * 100) : 0;

    return {
      totalBeds,
      availableBeds,
      occupiedBeds,
      cleaningBeds,
      maintenanceBeds,
      totalRooms,
      totalWards,
      occupancyRate,
    };
  };

  const stats = getStats();

  // Get ward-wise statistics
  const getWardStats = () => {
    if (!bedsData || !wardsData) return [];

    // Handle paginated responses
    const beds = Array.isArray(bedsData) ? bedsData : bedsData?.results || [];
    const wards = Array.isArray(wardsData) ? wardsData : wardsData?.results || [];

    return wards.map((ward) => {
      const wardBeds = beds.filter((bed) => bed.ward_name === ward.name);
      const available = wardBeds.filter((bed) => bed.status === 1).length;
      const occupied = wardBeds.filter((bed) => bed.status === 2).length;
      const total = wardBeds.length;
      const occupancyRate =
        total > 0 ? Math.round((occupied / total) * 100) : 0;

      return {
        wardName: ward.name,
        wardCode: ward.code,
        total,
        available,
        occupied,
        occupancyRate,
      };
    });
  };

  const wardStats = getWardStats();

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography color="error">
          {t("bedManagement.failedToLoad", {
            defaultValue: "Failed to load dashboard data",
          })}
        </Typography>
      </Box>
    );
  }

  // Create widget data
  const bedWidgets = [
    {
      icon: <Bed />,
      title: t("bedManagement.dashboard.total_beds", { defaultValue: "Total Beds" }),
      value: stats.totalBeds,
      amount: `${stats.totalWards} wards, ${stats.totalRooms} rooms`,
      history: [],
      cardSx: {
        bgcolor: "#1976d2",
        color: "#fff",
        borderLeft: "#1976d2",
      },
    },
    {
      icon: <CheckCircle />,
      title: t("bedManagement.dashboard.available", { defaultValue: "Available" }),
      value: stats.availableBeds,
      amount: `${Math.round((stats.availableBeds / stats.totalBeds) * 100)}% of total`,
      history: [],
      cardSx: {
        bgcolor: "#2e7d32",
        color: "#fff",
        borderLeft: "#2e7d32",
      },
    },
    {
      icon: <LocalHospital />,
      title: t("bedManagement.dashboard.occupied", { defaultValue: "Occupied" }),
      value: stats.occupiedBeds,
      amount: `${Math.round((stats.occupiedBeds / stats.totalBeds) * 100)}% occupancy`,
      history: [],
      cardSx: {
        bgcolor: "#d32f2f",
        color: "#fff",
        borderLeft: "#d32f2f",
      },
    },
    {
      icon: <TrendingUp />,
      title: t("bedManagement.dashboard.occupancy_rate", { defaultValue: "Occupancy Rate" }),
      value: `${stats.occupancyRate}%`,
      amount: stats.occupancyRate > 80 ? "High occupancy" : stats.occupancyRate > 60 ? "Moderate occupancy" : "Low occupancy",
      history: [],
      cardSx: {
        bgcolor: stats.occupancyRate > 80 ? "#d32f2f" : stats.occupancyRate > 60 ? "#f57c00" : "#2e7d32",
        color: "#fff",
        borderLeft: stats.occupancyRate > 80 ? "#d32f2f" : stats.occupancyRate > 60 ? "#f57c00" : "#2e7d32",
      },
    },
    {
      icon: <Warning />,
      title: t("bedManagement.dashboard.cleaning", { defaultValue: "Cleaning" }),
      value: stats.cleaningBeds,
      amount: `${stats.cleaningBeds} beds being cleaned`,
      history: [],
      cardSx: {
        bgcolor: "#f57c00",
        color: "#fff",
        borderLeft: "#f57c00",
      },
    },
    {
      icon: <Hotel />,
      title: t("bedManagement.dashboard.maintenance", { defaultValue: "Maintenance" }),
      value: stats.maintenanceBeds,
      amount: `${stats.maintenanceBeds} beds under maintenance`,
      history: [],
      cardSx: {
        bgcolor: "#7b1fa2",
        color: "#fff",
        borderLeft: "#7b1fa2",
      },
    },
  ];

  const handleWidgetClick = (title) => {
    console.log(`Clicked on ${title} widget`);
    // You can add navigation or modal opening logic here
  };

  return (
    <Box>
      {/* Header */}
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: "bold" }}>
        {t("bedManagement.dashboard.title", {
          defaultValue: "Bed Management Dashboard",
        })}
      </Typography>

      {/* Widget Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {bedWidgets.map((widget, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <BedWidget
              icon={widget.icon}
              title={widget.title}
              value={widget.value}
              amount={widget.amount}
              history={widget.history}
              onClick={() => handleWidgetClick(widget.title)}
              cardSx={widget.cardSx}
            />
          </Grid>
        ))}
      </Grid>

      {/* Ward-wise Statistics */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t("bedManagement.dashboard.ward_occupancy", {
            defaultValue: "Ward-wise Occupancy",
          })}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  {t("bedManagement.dashboard.ward", { defaultValue: "Ward" })}
                </TableCell>
                <TableCell align="center">
                  {t("bedManagement.dashboard.total_beds", {
                    defaultValue: "Total Beds",
                  })}
                </TableCell>
                <TableCell align="center">
                  {t("bedManagement.dashboard.available", {
                    defaultValue: "Available",
                  })}
                </TableCell>
                <TableCell align="center">
                  {t("bedManagement.dashboard.occupied", {
                    defaultValue: "Occupied",
                  })}
                </TableCell>
                <TableCell align="center">
                  {t("bedManagement.dashboard.occupancy_rate", {
                    defaultValue: "Occupancy Rate",
                  })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {wardStats.map((ward, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {ward.wardName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {ward.wardCode}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{ward.total}</TableCell>
                  <TableCell align="center">
                    <Typography color="success.main" fontWeight="bold">
                      {ward.available}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography color="error.main" fontWeight="bold">
                      {ward.occupied}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${ward.occupancyRate}%`}
                      color={
                        ward.occupancyRate > 80
                          ? "error"
                          : ward.occupancyRate > 60
                          ? "warning"
                          : "success"
                      }
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default BedDashboard;
